export type ColorThemePreference = 'system' | 'light' | 'dark';

export const COLOR_THEME_COOKIE = 'vetsy_theme_shared';
export const COLOR_THEME_PREFERENCES: readonly ColorThemePreference[] = ['system', 'light', 'dark'];

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function isPreference(value: unknown): value is ColorThemePreference {
  return COLOR_THEME_PREFERENCES.includes(value as ColorThemePreference);
}

export function useColorTheme() {
  const hostname = useRequestURL().hostname;
  const mainDomain = String(useRuntimeConfig().public.mainDomain || hostname.replace(/^shop\./, ''))
    .replace(/:\d+$/, '').toLowerCase();
  const domain = mainDomain.includes('.') && (hostname === mainDomain || hostname.endsWith(`.${mainDomain}`))
    ? mainDomain
    : undefined;
  const legacyCookie = useCookie<string | null>('vetsy_theme', { readonly: true });
  const cookie = useCookie<string | null>(COLOR_THEME_COOKIE, {
    default: () => hostname === mainDomain && isPreference(legacyCookie.value) ? legacyCookie.value : null,
    domain,
    maxAge: ONE_YEAR_SECONDS,
    sameSite: 'lax',
    path: '/',
  });

  const state = useState<ColorThemePreference>('color-theme-preference', () => (
    isPreference(cookie.value) ? cookie.value : 'light'
  ));

  const preference = computed<ColorThemePreference>({
    get: () => state.value,
    set: (value) => {
      state.value = value;
      cookie.value = value;
    },
  });

  const route = useRoute();
  const explicitTheme = computed<'light' | 'dark' | undefined>(() => {
    if (route.meta.layout === 'widget') {
      return 'light';
    }

    return preference.value === 'system' ? undefined : preference.value;
  });

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const isDark = computed(() => (explicitTheme.value ? explicitTheme.value === 'dark' : prefersDark.value));
  const darkMedia = computed(() => {
    if (!explicitTheme.value) {
      return '(prefers-color-scheme: dark)';
    }

    return explicitTheme.value === 'dark' ? 'all' : 'not all';
  });

  return { preference, explicitTheme, isDark, darkMedia };
}

export function useColorThemeHead() {
  const { preference, explicitTheme } = useColorTheme();

  useHead({
    htmlAttrs: {
      'data-theme-preference': () => preference.value,
      'data-theme': () => explicitTheme.value,
    },
    meta: [
      { name: 'color-scheme', content: () => explicitTheme.value ?? 'light dark' },
    ],
  });
}
